/**
 * Hook for managing pending files during ticket creation
 * Files are stored locally until the ticket is created, then uploaded
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { QmsPendingFile } from '../types/qms.types';

// Generate a simple UUID for client-side file identification
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Check if a file is an image by MIME type
function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

export interface UsePendingFilesReturn {
  /** List of pending files */
  pendingFiles: QmsPendingFile[];
  /** Add files to the pending list */
  addFiles: (files: FileList | File[]) => void;
  /** Remove a file by its ID */
  removeFile: (id: string) => void;
  /** Clear all pending files */
  clearFiles: () => void;
  /** Update a file's status */
  updateFileStatus: (id: string, status: QmsPendingFile['status'], error?: string) => void;
  /** Check if there are any pending files */
  hasPendingFiles: boolean;
  /** Total size of all pending files */
  totalSize: number;
}

export function usePendingFiles(): UsePendingFilesReturn {
  const [pendingFiles, setPendingFiles] = useState<QmsPendingFile[]>([]);
  // Use a ref to track files for cleanup on unmount (avoids stale closure)
  const pendingFilesRef = useRef<QmsPendingFile[]>([]);

  // Keep ref in sync with state
  useEffect(() => {
    pendingFilesRef.current = pendingFiles;
  }, [pendingFiles]);

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      // Revoke all preview URLs on unmount using the ref (current value)
      pendingFilesRef.current.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, []); // Only on unmount

  const addFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);

    const newPendingFiles: QmsPendingFile[] = fileArray.map((file) => {
      const isImage = isImageFile(file);
      return {
        id: generateId(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: isImage ? URL.createObjectURL(file) : undefined,
        status: 'pending' as const,
      };
    });

    setPendingFiles((prev) => [...prev, ...newPendingFiles]);
  }, []);

  const removeFile = useCallback((id: string) => {
    setPendingFiles((prev) => {
      const fileToRemove = prev.find((f) => f.id === id);
      // Revoke the preview URL if it exists
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const clearFiles = useCallback(() => {
    setPendingFiles((prev) => {
      // Revoke all preview URLs
      prev.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
      return [];
    });
  }, []);

  const updateFileStatus = useCallback(
    (id: string, status: QmsPendingFile['status'], error?: string) => {
      setPendingFiles((prev) =>
        prev.map((file) =>
          file.id === id
            ? { ...file, status, error: error || undefined }
            : file
        )
      );
    },
    []
  );

  const hasPendingFiles = pendingFiles.length > 0;
  const totalSize = pendingFiles.reduce((sum, file) => sum + file.size, 0);

  return {
    pendingFiles,
    addFiles,
    removeFile,
    clearFiles,
    updateFileStatus,
    hasPendingFiles,
    totalSize,
  };
}
